import { Copy, Edit, Ellipsis, Eye, Trash2 } from "lucide-react"
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@components/common/menu-bar"
import { DictType } from "@pma/types"
import { Separator } from "@components/common/separator"
import useTranslator from "@hooks/use-translator"
import { useContext, useState } from "react"
import { cn } from "@pma/utils/helpers"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { cloneWorkDiaryService } from "@pma/services/task"
import { toast } from "@pma/hooks/use-toast"
import DeleteWorkDiaries from "./delete-work-diaries"
import WorkDiaryDetails from "./work-diary-details"
import CloneConfirmDialog from "@pma/components/workDiary/CloneConfirmDialog"
import { WorkDiaryContext } from "./work-diaries-list"
import { useAuth } from "@pma/stores/auth-store"
import RestoreWorkDiary from "./restore-work-diary"

interface Props {
  project: DictType
  workDiaries: DictType
  styled?: boolean
}
const WorkDiariesDetailMenu = ({ project, workDiaries, styled = true }: Props) => {
  const { authConfig } = useAuth()

  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const queryClient = useQueryClient()
  const [selectedWorkDiary, setSelectedWorkDiary] = useState<DictType | null>(null)
  const [workDiaryDetailsModal, setWorkDiaryDetailsModal] = useState(false)
  const [cloneConfirmDiary, setCloneConfirmDiary] = useState<DictType | null>(null)
  const [cloneDate, setCloneDate] = useState<string>("")
  const { setSelectedWorkDiaryDetails } = useContext(WorkDiaryContext)

  const cloneWorkDiaryMutation = useMutation({
    mutationKey: ["clone-work-diary"],
    mutationFn: cloneWorkDiaryService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
      toast({
        title: translate("Success"),
        description: `${cloneConfirmDiary?.number} ${translate("has been cloned successfully")}`,
        variant: "success",
      })
      setCloneConfirmDiary(null)
      setCloneDate("")
    },
    onError: (error: any) => {
      const errorMessage = error?.message || error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || `${translate("Unable to clone")} ${translate("Work Diary")}`,
        variant: "error",
      })
    },
  })

  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const openModal = () => setSelectedWorkDiaryDetails(workDiaries)

  const openDetailsModal = () => {
    setSelectedWorkDiary(workDiaries)
    setWorkDiaryDetailsModal(true)
  }

  return (
    <>
      <Menubar className='border-none bg-transparent'>
        <MenubarMenu>
          <Eye
            size={20}
            className={cn("cursor-pointer hover:text-primary", isHebrew ? "ml-2" : "mr-2")}
            onClick={(e) => {
              e.preventDefault()
              openDetailsModal()
            }}
          />
          <MenubarTrigger
            className={cn(
              "flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border p-0",
              styled && "bg-primary text-white",
              !styled && "hover:bg-transparent",
            )}
          >
            <Ellipsis size={20} />
          </MenubarTrigger>
          <MenubarContent className={cn("mx-2 gap-2 bg-background", isHebrew ? "rtl" : "ltr")}>
            {workDiaries.deleted &&
            (isOrganizationOwner || isAdmin || workDiaries.deleted_by === authConfig.id) ? (
              <>
                <MenubarItem
                  onClick={(e) => e.preventDefault()}
                  className='flex items-center p-1 text-xs text-red-500'
                >
                  <RestoreWorkDiary
                    workDiaries={workDiaries}
                    className='flex w-full flex-row items-center justify-between'
                  >
                    <div className='flex w-full items-center'>
                      <span
                        className={cn(
                          "min-w-[180px] flex-1",
                          isHebrew ? "text-right" : "text-left",
                        )}
                      >
                        {translate("Restore Diary")}
                      </span>
                    </div>
                  </RestoreWorkDiary>
                </MenubarItem>
                <Separator />
              </>
            ) : !workDiaries.delete ? (
              <>
                <MenubarItem
                  onClick={(e) => {
                    e.preventDefault()
                    openModal()
                  }}
                  className='flex items-center p-1 text-xs'
                >
                  <div className='flex w-full items-center'>
                    <span
                      className={cn("min-w-[180px] flex-1", isHebrew ? "text-right" : "text-left")}
                    >
                      {translate("Edit")}
                    </span>
                    <Edit className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                  </div>
                </MenubarItem>
                <Separator />
                <MenubarItem
                  onClick={(e) => {
                    e.preventDefault()
                    openDetailsModal()
                  }}
                  className='flex items-center p-1 text-xs'
                >
                  <div className='flex w-full items-center'>
                    <span
                      className={cn("min-w-[180px] flex-1", isHebrew ? "text-right" : "text-left")}
                    >
                      {translate("View Details")}
                    </span>
                    <Eye className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                  </div>
                </MenubarItem>
                <Separator />
                <MenubarItem
                  onClick={(e) => {
                    e.preventDefault()
                    setCloneConfirmDiary(workDiaries)
                    setCloneDate("")
                  }}
                  className='flex items-center p-1 text-xs'
                >
                  <div className='flex w-full items-center'>
                    <span
                      className={cn("min-w-[180px] flex-1", isHebrew ? "text-right" : "text-left")}
                    >
                      {translate("Duplicate")}
                    </span>
                    <Copy className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                  </div>
                </MenubarItem>
                <Separator />
                <MenubarItem
                  onClick={(e) => e.preventDefault()}
                  className='flex items-center p-1 text-xs text-red-500'
                >
                  <DeleteWorkDiaries
                    workDiaries={workDiaries}
                    className='flex w-full flex-row items-center justify-between'
                  >
                    <div className='flex w-full items-center'>
                      <span
                        className={cn(
                          "min-w-[180px] flex-1",
                          isHebrew ? "text-right" : "text-left",
                        )}
                      >
                        {translate("Delete")}
                      </span>
                      <Trash2 className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                    </div>
                  </DeleteWorkDiaries>
                </MenubarItem>
                <Separator />
              </>
            ) : null}
          </MenubarContent>
        </MenubarMenu>
      </Menubar>

      {workDiaryDetailsModal && selectedWorkDiary && (
        <WorkDiaryDetails
          project={project}
          workDiaryDetailsModal={workDiaryDetailsModal}
          selectedWorkDiary={selectedWorkDiary}
          setWorkDiaryDetailsModal={setWorkDiaryDetailsModal}
          setSelectedWorkDiary={setSelectedWorkDiary}
        />
      )}

      <CloneConfirmDialog
        cloneConfirmDiary={cloneConfirmDiary}
        setCloneConfirmDiary={setCloneConfirmDiary}
        cloneDate={cloneDate}
        setCloneDate={setCloneDate}
        isPending={cloneWorkDiaryMutation.isPending}
        onClone={(diary_id, date) => cloneWorkDiaryMutation.mutate({ diary_id, date })}
      />
    </>
  )
}

export default WorkDiariesDetailMenu
